import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../services/api';
import { Campaign } from '../../types';

// Query hooks
export const useCampaignsQuery = () => {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: api.fetchCampaigns,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCampaignQuery = (campaignId: string | undefined) => {
  return useQuery({
    queryKey: ['campaigns', campaignId],
    queryFn: () => {
      if (!campaignId) throw new Error('Campaign ID is required');
      return api.fetchCampaignById(campaignId);
    },
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCampaignStatusQuery = (campaignId: string | undefined) => {
  return useQuery({
    queryKey: ['campaigns', campaignId, 'status'],
    queryFn: () => {
      if (!campaignId) throw new Error('Campaign ID is required');
      return api.fetchCampaignStatus(campaignId);
    },
    enabled: !!campaignId,
    staleTime: 2 * 60 * 1000, // Status changes more frequently
  });
};

// Mutation hooks
export const useCreateCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.createCampaign,
    onSuccess: (newCampaign: Campaign) => {
      // Invalidate campaigns list to trigger a fresh fetch
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      // Also set the individual campaign data in case user navigates to it
      queryClient.setQueryData(['campaigns', newCampaign.campaign_id], newCampaign);
    },
    onError: () => {
      // Refresh campaigns list on error to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

export const useUpdateCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: Partial<Campaign> }) =>
      api.updateCampaign(campaignId, data),
    onSuccess: (updatedCampaign: Campaign, { campaignId }) => {
      // Update individual campaign cache
      queryClient.setQueryData(['campaigns', campaignId], updatedCampaign);
      // Update campaigns list
      queryClient.setQueryData(['campaigns'], (old: Campaign[] = []) =>
        old.map(c => (c.campaign_id === campaignId ? updatedCampaign : c))
      );
      // Update status cache if it exists
      if (updatedCampaign.status) {
        queryClient.setQueryData(['campaigns', campaignId, 'status'], updatedCampaign.status);
      }
    },
    onError: (_, { campaignId }) => {
      // Refresh specific campaign data on error
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

export const useDeleteCampaignMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.deleteCampaign,
    onSuccess: (_, campaignId: string) => {
      // Remove from campaigns list
      queryClient.setQueryData(['campaigns'], (old: Campaign[] = []) =>
        old.filter(c => c.campaign_id !== campaignId)
      );
      // Remove individual campaign cache
      queryClient.removeQueries({ queryKey: ['campaigns', campaignId] });
    },
    onError: () => {
      // Refresh campaigns list on error
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

export const useUpdateCampaignStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, status }: { campaignId: string; status: 'enabled' | 'disabled' }) =>
      api.updateCampaignStatus(campaignId, status),
    onSuccess: (_, { campaignId, status }) => {
      // Optimistic update to campaigns list
      queryClient.setQueryData(['campaigns'], (old: Campaign[] = []) =>
        old.map(c => (c.campaign_id === campaignId ? { ...c, status } : c))
      );
      // Update individual campaign cache
      queryClient.setQueryData(['campaigns', campaignId], (old: Campaign | undefined) =>
        old ? { ...old, status } : old
      );
      // Update status cache
      queryClient.setQueryData(['campaigns', campaignId, 'status'], status);
    },
    onError: (_, { campaignId }) => {
      // Refresh campaign data on error
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};
